import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';

import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { DOCUMENTS_ROUTING_PARAMS, DocumentsPaths } from 'app/configs/routesConfig/documents';
import { documentsApi } from 'app/services/api';
import { AlignIcon, OpenIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import LabelCDS from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS';
import { LabelSize } from 'app/shared/componentsRestyled/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { formatDate } from 'utils/date';
import { getFileDateTimeName } from 'utils/file';
import { logger } from 'utils/logger';
import { downloadOrShareBlob } from 'utils/nativeFile';
import { openURLInNewTab } from 'utils/navigation';

import { showErrorMessageAction } from 'store/global/actions';

import { MilestoneRoadmapDocumentsTestIds } from '../../MilestoneRoadmapDocuments.testIds';

import {
  DOWNLOAD_LABEL,
  OPEN_IN_NEW_TAB_LABEL,
  SENT_TO_BORROWER_LABEL,
} from './MilestoneRoadmapDocumentRow.constants';
import {
  actionButtonStyles,
  actionsStyles,
  dateStyles,
  getRowStyles,
  metaRowStyles,
  nameStyles,
} from './MilestoneRoadmapDocumentRow.styles';

const MilestoneRoadmapDocumentRow = ({ document, isFirst = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const onOpenDocument = useCallback(() => {
    const href = history.createHref({
      pathname: DocumentsPaths.documentPreview.generatePath({
        [DOCUMENTS_ROUTING_PARAMS.docId]: document.id,
      }),
    });

    openURLInNewTab(href);
  }, [document.id, history]);

  const onOpenClick = useCallback(
    e => {
      e.stopPropagation();
      onOpenDocument();
    },
    [onOpenDocument],
  );

  const onDownloadClick = useCallback(
    async e => {
      e.stopPropagation();

      try {
        const fileName = getFileDateTimeName({ fileName: document.name, extension: 'pdf' });
        const { data: pdfBlob } = await documentsApi.getContent({ documentId: document.id });

        await downloadOrShareBlob(pdfBlob, fileName);
      } catch (err) {
        dispatch(showErrorMessageAction({ message: 'Could not download document', err }));
        logger.error(err);
      }
    },
    [dispatch, document.id, document.name],
  );

  return (
    <Box
      data-testid={MilestoneRoadmapDocumentsTestIds.ROW}
      sx={getRowStyles(isFirst)}
      onClick={onOpenDocument}
    >
      <Typography sx={nameStyles} title={document.name} variant="medium12">
        {document.name}
      </Typography>

      <Box sx={metaRowStyles}>
        <Typography sx={dateStyles} variant="regular12">
          {formatDate(document.attachedDate, SHORT_MONTH_DAY_YEAR_FORMAT)}
        </Typography>

        {document.isSentToBorrower && (
          <LabelCDS size={LabelSize.S} title={SENT_TO_BORROWER_LABEL} />
        )}

        <Box sx={actionsStyles}>
          <Tooltip title={DOWNLOAD_LABEL}>
            <ButtonBase
              aria-label={DOWNLOAD_LABEL}
              data-testid={MilestoneRoadmapDocumentsTestIds.ROW_DOWNLOAD}
              sx={actionButtonStyles}
              onClick={onDownloadClick}
            >
              <AlignIcon size={16} />
            </ButtonBase>
          </Tooltip>

          <Tooltip title={OPEN_IN_NEW_TAB_LABEL}>
            <ButtonBase
              aria-label={OPEN_IN_NEW_TAB_LABEL}
              data-testid={MilestoneRoadmapDocumentsTestIds.ROW_OPEN}
              sx={actionButtonStyles}
              onClick={onOpenClick}
            >
              <OpenIcon size={14} />
            </ButtonBase>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

MilestoneRoadmapDocumentRow.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    attachedDate: PropTypes.string,
    isSentToBorrower: PropTypes.bool,
  }).isRequired,
  isFirst: PropTypes.bool,
};

export default MilestoneRoadmapDocumentRow;
